import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preview-only auto-login. Active only on the Emergent preview host so that
// production/custom domains continue to require a real sign-in.
const PREVIEW_DEMO_EMAIL = 'Businessorganizationalbestie@gmail.com';
const PREVIEW_DEMO_PASSWORD = '1982Peterpan2';
const isPreviewHost = () =>
  typeof window !== 'undefined' &&
  window.location.hostname.endsWith('.preview.emergentagent.com');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const [profileRes, subRes] = await Promise.all([
            axios.get(`${API}/profile`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${token}` } })
          ]);
          setUser(profileRes.data);
          setSubscription(subRes.data);
          setDarkMode(profileRes.data.dark_mode || false);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      } else if (isPreviewHost()) {
        // Preview convenience: auto sign-in as demo admin so the app can be
        // browsed without the login page. This is gated to the Emergent preview
        // host only, so production/custom domains still require real sign-in.
        try {
          const response = await axios.post(`${API}/auth/login`, {
            email: PREVIEW_DEMO_EMAIL,
            password: PREVIEW_DEMO_PASSWORD
          });
          const { token: newToken, user: userData } = response.data;
          localStorage.setItem('token', newToken);
          const subRes = await axios.get(`${API}/subscription/status`, {
            headers: { Authorization: `Bearer ${newToken}` }
          });
          setUser(userData);
          setSubscription(subRes.data);
          setDarkMode(userData.dark_mode || false);
          setToken(newToken);
          return; // token change re-triggers this effect
        } catch (error) {
          console.error('Preview auto-login failed:', error);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const checkSubscription = async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
      return response.data;
    } catch (error) {
      console.error('Subscription check error:', error);
      return null;
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { 
      email, 
      password
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    
    // Check subscription status after login
    const subRes = await axios.get(`${API}/subscription/status`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    setSubscription(subRes.data);
    
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    
    // New users get trial, check status
    const subRes = await axios.get(`${API}/subscription/status`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    setSubscription(subRes.data);
    
    return response.data;
  };

  const logout = () => {
    // Best-effort: clear the httpOnly cookie on the server. Don't block UI on failure.
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setSubscription(null);
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, { 
      token: resetToken, 
      new_password: newPassword 
    });
    return response.data;
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(response.data);
    if (data.dark_mode !== undefined) {
      setDarkMode(data.dark_mode);
    }
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    if (token) {
      updateProfile({ dark_mode: newMode });
    }
  };

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const hasActiveSubscription = subscription?.is_active || false;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      darkMode,
      subscription,
      hasActiveSubscription,
      login,
      register,
      logout,
      forgotPassword,
      resetPassword,
      changePassword,
      updateProfile,
      toggleDarkMode,
      getAuthHeaders,
      checkSubscription,
      isAuthenticated: !!token
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
